import { apiSlice } from './apiSlice';
import { colors } from '@headless-shell/shared-styles';

export const generateMyAppsWithStatusObject = (myApps = []) => {
  myApps.forEach((myApp, index) => {
    const {
      applicationId,
      payUrl,
      daysUntilPayment,
      remainingFreeTrialDays,
      companyEntitlementStatus,
      companyEntitlementSuspensionReason,
      startDate,
      state
    } = myApp;

    const isValidStartDate = startDate && !isNaN(new Date(startDate).getTime());
    const startdate = isValidStartDate ? new Date(startDate).toLocaleDateString() : '';

    const companyEntitlementStatusMessageObject = {
      INITIALIZED: {
        statusKey: 'accountPending',
        message: 'App Initialized',
        color: colors.MYAPPGRAY,
        status: 'new',
        btnDisabled: true
      },
      FREE_TRIAL_EXPIRED: {
        statusKey: 'freeTrialExpired',
        message: 'Free Trial Expired',
        color: colors.MYAPPRED,
        status: 'failed',
        url: {
          href: `/app/${applicationId}/detail`,
          embeddableHref: true
        }
      },
      FAILED: {
        statusKey: 'paymentDue',
        message: 'Payment Failed',
        color: colors.MYAPPRED,
        status: 'changed',
        days: daysUntilPayment,
        url: {
          text: 'Buy Now',
          href: payUrl
        }
      },
      ACTIVE: {
        statusKey: 'subscriptionActive',
        message: 'Subscription Active',
        color: colors.gray.shadow,
        status: 'default'
      }
    };
    const companyEntitlementSuspensionReasonObject = {
      PAYMENT_OVERDUE: {
        statusKey: 'paymentOverdue',
        message: 'Payment Overdue',
        color: colors.MYAPPRED,
        status: 'failed',
        days: Math.abs(daysUntilPayment),
        url: {
          text: 'Pay Now',
          href: payUrl
        }
      },
      END_OF_CONTRACT: {
        statusKey: 'contractEnded',
        message: 'Contract Ended',
        color: colors.MYAPPRED,
        status: 'failed',
        url: {
          text: 'Renew Now',
          href: payUrl
        }
      },
      CONTRACT_EXTENSION_FAILURE: {
        statusKey: 'contractEnded',
        message: 'Contract Ended',
        color: colors.MYAPPRED,
        status: 'failed'
      },
      UNSUCCESSFUL_PAYMENT: {
        statusKey: 'paymentOverdue',
        message: 'Payment Failed',
        color: colors.MYAPPRED,
        days: Math.abs(daysUntilPayment),
        status: 'failed',
        url: {
          text: 'Pay Now',
          href: payUrl
        }
      },
      MANUALLY_SUSPENDED: {
        statusKey: 'cancellationPending',
        message: 'Manually Suspended',
        color: colors.MYAPPRED,
        status: 'new',
        btnDisabled: true
      },
      API: {
        statusKey: 'cancellationPending',
        message: 'App is suspended',
        color: colors.MYAPPRED,
        status: 'new',
        btnDisabled: true
      },
      PARENT_SUBSCRIPTION_SUSPENDED: {
        statusKey: 'cancellationPending',
        message: 'Parent Subscription Suspended',
        color: colors.MYAPPRED,
        status: 'new',
        btnDisabled: true
      }
    };
    let statusObject = null;

    if (~Object.keys(companyEntitlementStatusMessageObject).indexOf(companyEntitlementStatus)) {
      statusObject = companyEntitlementStatusMessageObject[companyEntitlementStatus];
    } else if (companyEntitlementStatus === 'SUSPENDED') {
      statusObject = companyEntitlementSuspensionReasonObject[companyEntitlementSuspensionReason];
    }

    if (!statusObject && remainingFreeTrialDays > 0 && remainingFreeTrialDays <= 5) {
      statusObject = {
        statusKey: 'freeTrialActive',
        warning: true,
        days: remainingFreeTrialDays,
        message: `Free Trial Expiring in ${remainingFreeTrialDays} days`,
        color: colors.MYAPPYELLOW,
        status: 'changed',
        url: {
          text: 'Buy Now',
          href: payUrl
        }
      };
    }

    if (!statusObject && daysUntilPayment > 0 && daysUntilPayment <= 5) {
      statusObject = {
        statusKey: 'paymentDue',
        warning: true,
        days: daysUntilPayment,
        message: `Payment Due in ${daysUntilPayment} days`,
        color: colors.MYAPPYELLOW,
        status: 'changed',
        url: {
          text: 'Pay Now',
          href: payUrl
        }
      };
    }

    if (!statusObject || statusObject.warning) {
      if (state === 'FREE_TRIAL' || state === 'FREE_TRIAL_EXPIRING') {
        statusObject = {
          statusKey: 'freeTrialActive',
          warning: true,
          days: remainingFreeTrialDays,
          message: `Free trial active through ${startdate}`,
          color: colors.MYAPPRED,
          status: 'changed'
        };
      }
    }

    if (!statusObject) {
      statusObject = { statusKey: 'unknown' };
    }

    if (statusObject) {
      myApps[index] = { ...myApp, statusObject };
    }
  });

  myApps.sort((a, b) => {
    return a.startDate > b.startDate ? -1 : 1;
  });

  return myApps;
};

export const myAppsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMyApps: builder.query({
      query: () => ({
        url: '/api/myapps',
        method: 'GET'
      }),
      providesTags: ['MyApps'],
      transformResponse: (response) => {
        const rawList = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const visibleApps = rawList.filter(
          (myApp) => myApp.pendingAppType !== 'REQUESTED'
        );
        const myAppsWithStatusObject = generateMyAppsWithStatusObject(visibleApps);
        const myAppNotificationBubble = myAppsWithStatusObject.filter(
          (myApp) => myApp.statusObject
        ).length;
        return {
          myapps: myAppsWithStatusObject,
          myAppNotificationBubble
        };
      },
      transformErrorResponse: (response) => {
        console.error('Failed to fetch my apps:', response?.data || 'API Error');
        return {
          myapps: [],
          myAppNotificationBubble: 0
        };
      }
    })
  }),
  overrideExisting: false
});

export const { useGetMyAppsQuery } = myAppsApi;